import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import useWebSocket from "react-use-websocket";
import {
  getData,
  getWSAuth,
  newPath
} from "../../../../components/utilities/UtilityFunctions";
import {
  getWebsocketUrl,
  handlePlayersChange,
  userDisplayName
} from "../EventFunctions";
import { postNotification } from "../../../../components/utilities/Notifications";
import { getArmyPickerArmies } from "../../../../components/api/APIUtilities";
import EventRosterDefault from "./EventRosterDefault";

const WEBSOCKET_URL = getWebsocketUrl();

export default function EventRosterWS(props) {
  const { organize, setActiveTab, event, authContext } = props;

  const navigate = useNavigate();
  const { t } = useTranslation("translation", {
    keyPrefix: "components.EventRoster",
  });

  const [roster, setRoster] = useState(null);

  const [armies, setArmies] = useState(null);
  //eslint-disable-next-line
  const [refreshPlayers, setRefreshPlayers] = useState(false);

  const [loading, setLoading] = useState(false);
  const [subLoading, setSubLoading] = useState(true);

  const [player, setPlayer] = useState(null);
  const [teamPlayer, setTeamPlayer] = useState(null);

  const [teamlessPlayers, setTeamlessPlayers] = useState([]);

  // Modal useStates
  const [optionsAnchorEl, setOptionsAnchorEl] = useState(null);
  const [bulkAddOpen, setBulkAddOpen] = useState(false);
  const [bulkAddEmailsOpen, setBulkAddEmailsOpen] = useState(false);

  // Filtering useStates
  const [filtersAnchorEl, setFiltersAnchorEl] = useState(null);
  const [filteredPlayers, setFilteredPlayers] = useState(null);
  const [filterType, setFilterType] = useState("");

  // WebSockets

  // Player websockets
  const [pSocketUrl, setPSocketUrl] = useState("");
  const [pSocketStatus, setPSocketStatus] = useState("");
  const pWebSocket = useWebSocket(pSocketUrl, {
    share: true,
    shouldReconnect: false,
    onOpen: () => setPSocketStatus("Live"),
    onClose: () => {
      if (pSocketUrl && pSocketUrl !== "" && pSocketStatus !== "Connecting") {
        setPSocketStatus("Disconnected");
      }
    }
  });

  // Team websockets
  const [tSocketUrl, setTSocketUrl] = useState("");
  const [tSocketStatus, setTSocketStatus] = useState("");
  const tWebSocket = useWebSocket(tSocketUrl, {
    share: true,
    shouldReconnect: false,
    onOpen: () => setTSocketStatus("Live"),
    onClose: () => {
      if (
        (event?.doublesEvent || event?.teamEvent) &&
        tSocketUrl &&
        tSocketUrl !== "" &&
        tSocketStatus !== "Connecting"
      ) {
        setTSocketStatus("Disconnected");
      }
    }
  });

  // Player stats / counters
  const [playerStats, setPlayerStats] = useState([
    {
      label: event?.teamEvent ? t("registeredTeams") : t("registeredPlayers"),
      value: "##",
      color: "info"
    },
    {
      label: event?.teamEvent ? t("checkedInTeams") : t("checkedInPlayers"),
      value: "##",
      color: "warning"
    },
    {
      label: event?.teamEvent ? t("activeTeams") : t("activePlayers"),
      value: "##",
      color: "success"
    }
  ]);

  // Utility functions
  const handleWSConnection = async () => {
    setPSocketStatus("Connecting");
    if (event?.doublesEvent || event?.teamEvent) setTSocketStatus("Connecting");

    const setSocketUrl = (endpoint, setFunc) =>
      setFunc(`${WEBSOCKET_URL}?connectionToken=${endpoint}`);

    let options = {
      eventId: event.id
    };

    if (pSocketUrl === "") {
      options.connectionType = "eventPlayerRoster";

      const pSocketAuth = await getWSAuth(options);
      setSocketUrl(pSocketAuth, setPSocketUrl);
    }

    if ((event?.doublesEvent || event?.teamEvent) && tSocketUrl === "") {
      options.connectionType = "eventTeamPlayerRoster";

      const tSocketAuth = await getWSAuth(options);
      setSocketUrl(tSocketAuth, setTSocketUrl);
    }
  };

  // Handles incoming messages and updates roster accordingly
  const handleMessages = (webSocket) => {
    if (webSocket?.lastMessage !== null) {
      const lastMessage = JSON.parse(webSocket.lastMessage?.data);
      let tempRoster = roster;

      let found = false;

      if (lastMessage?.status === "updated") {
        if (lastMessage?.data) {
          const currentPlayer = lastMessage.data;

          if (event?.teamEvent || event?.doublesEvent) {
            handlePlayersInTeams(tempRoster, currentPlayer);
          } else {
            if (tempRoster && tempRoster?.length) {
              tempRoster.forEach((tempPlayer, i) => {
                if (
                  (tempPlayer?.user?.id || tempPlayer?.userId) ===
                  (currentPlayer?.user?.id || currentPlayer?.userId)
                ) {
                  tempRoster[i] = currentPlayer;
                  found = true;
                }
              });

              if (!found) {
                tempRoster = tempRoster.concat(currentPlayer);
              }
            } else {
              tempRoster = [currentPlayer];
            }
          }
        }
      }

      if (lastMessage?.status === "deleted") {
        if (tempRoster?.length) {
          if (event?.teamEvent || event?.doublesEvent) {
            tempRoster.forEach((team, index) => {
              if (team?.players?.length) {
                team.players.forEach((player, i) => {
                  if (
                    lastMessage?.id === player?.userId ||
                    lastMessage?.id === player?.user?.id ||
                    lastMessage?.id === player?.id
                  ) {
                    tempRoster[index].players.splice(i, 1);
                  }
                });
              }
            });
          } else {
            tempRoster.forEach((player, i) => {
              if (
                lastMessage?.id === player?.userId ||
                lastMessage?.id === player?.user?.id ||
                lastMessage?.id === player?.id
              ) {
                tempRoster.splice(i, 1);
              }
            });
          }
        }
      }

      if (tempRoster) {
        tempRoster.sort((a, b) => {
          var nameA = userDisplayName(
            a?.firstName || a?.user?.firstName,
            a.lastName
          ).toLowerCase();
          var nameB = userDisplayName(
            b?.firstName || b?.user?.firstName
          ).toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });

        setRoster([...tempRoster]);
      }
    }
  };

  const handleTeamMessages = (webSocket) => {
    if (webSocket?.lastMessage !== null) {
      const lastMessage = JSON.parse(webSocket.lastMessage?.data);
      let tempRoster = roster;

      let found = false;

      if (lastMessage?.status === "updated") {
        if (lastMessage?.data) {
          const currentTeam = lastMessage.data;

          if (tempRoster && tempRoster?.length) {
            tempRoster.forEach((team, i) => {
              if (team?.id === currentTeam?.id) {
                found = true;
                tempRoster[i] = { ...team, ...currentTeam };
              }
            });

            if (!found) {
              tempRoster.push(currentTeam);
            }
          } else {
            tempRoster = [currentTeam];
          }
        }
      }

      if (lastMessage?.status === "deleted") {
        if (tempRoster?.length) {
          tempRoster.forEach((team, i) => {
            if (
              lastMessage?.id === team?.userId ||
              lastMessage?.id === team?.user?.id ||
              lastMessage?.id === team?.id
            ) {
              tempRoster.splice(i, 1);
            }
          });
        }
      }

      if (tempRoster) {
        setRoster([...sortTeams(tempRoster)]);
      }
    }
  };

  // Function used when a player is deleted from roster
  const deletePlayer = (playerId) => {
    if (roster) {
      let tempRoster = [...roster];
      tempRoster.forEach((player, index) => {
        if (player?.id === playerId) {
          tempRoster.splice(index, 1);
        }
      });
      setRoster([...tempRoster]);
    }
  };

  const fetchPlayers = async () => {
    const returnArray = [];

    try {
      const apiData = await getData(`/events/${event.id}/players`);

      if (apiData && apiData?.active?.length) {
        returnArray.push(...apiData.active);
      }

      if (!returnArray || !returnArray?.length) {
        return [];
      }

      returnArray?.sort((a, b) => {
        var nameA = userDisplayName(
          a?.user?.firstName,
          a?.user?.lastName
        ).toLowerCase();
        var nameB = userDisplayName(
          b?.user?.firstName,
          b?.user?.lastName
        ).toLowerCase();
        if (nameA > nameB) return 1;
        if (nameA < nameB) return -1;
        return 0;
      });
    } catch (err) {
      console.error(err);
    }
    return returnArray;
  };

  const fetchTeamPlayers = async () => {
    var returnArray = [];
    try {
      const apiData = await getData(`/events/${event.id}/teamplayers`);

      returnArray = apiData?.active;

      if (!returnArray || !returnArray.length) {
        return [];
      }

      returnArray.sort((a, b) => {
        if (a.name?.toLowerCase() > b.name?.toLowerCase()) return 1;
        if (a.name?.toLowerCase() < b.name?.toLowerCase()) return -1;
        return 0;
      });
    } catch (err) {
      console.error(err);
      return undefined;
    }
    return returnArray;
  };

  async function parallel() {
    const taskPlayers = fetchPlayers();
    const taskTeamPlayers =
      event?.teamEvent || event?.doublesEvent ? fetchTeamPlayers() : null;

    return {
      players: await taskPlayers,
      teamPlayers: await taskTeamPlayers
    };
  }

  const handlePlayers = async () => {
    const parallelData = await parallel();

    if (
      (event?.teamEvent || event?.doublesEvent) &&
      parallelData.teamPlayers?.length &&
      parallelData.players?.length
    ) {
      // Adds players to teamPlayers
      parallelData.players.forEach((player) => {
        if (player?.teamPlayerId) {
          // Add player to teamPlayer
          parallelData.teamPlayers.forEach((teamPlayer) => {
            if (teamPlayer?.id === player.teamPlayerId) {
              if (!teamPlayer.players) {
                teamPlayer.players = [];
              }
              teamPlayer.players.push(player);
            }

            if (teamPlayer?.captainId === player.id) {
              teamPlayer.captain = player;
            }
          });
        }
      });

      // Sort players in teams by name
      parallelData.teamPlayers.forEach((teamPlayer) => {
        if (teamPlayer?.players && teamPlayer.players.length) {
          teamPlayer.players.sort((a, b) => {
            var nameA = userDisplayName(
              a?.user?.firstName,
              a?.user?.lastName
            ).toLowerCase();
            var nameB = userDisplayName(
              b?.user?.firstName,
              b?.user?.lastName
            ).toLowerCase();
            if (nameA > nameB) return 1;
            if (nameA < nameB) return -1;
            return 0;
          });
        }
      });
    }

    const { newPlayerArray } = handlePlayersChange(
      event,
      event?.teamEvent ? parallelData.teamPlayers : parallelData.players,
      {
        showOverall: false,
        isPlacings: false
      }
    );

    newPlayerArray.sort((a, b) => {
      var nameA = userDisplayName(
        a?.firstName || a?.user?.firstName,
        a.lastName
      ).toLowerCase();
      var nameB = userDisplayName(
        b?.firstName || b?.user?.firstName
      ).toLowerCase();
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;
      return 0;
    });

    setRoster(newPlayerArray);
  };

  const handlePlayersInTeams = (roster, currentPlayer, teamlessArr = false) => {
    if (roster?.length) {
      let hasTeam = false;
      let found = false;

      roster.forEach((team) => {
        if (team?.id === currentPlayer?.teamPlayerId) {
          hasTeam = true;

          if (team?.players?.length) {
            team.players.forEach((player, i) => {
              if (player.id === currentPlayer.id) {
                found = true;
                team.players[i] = currentPlayer;
              }
            });

            if (!found) {
              team.players.push(currentPlayer);
            }
          } else {
            team.players = [currentPlayer];
          }
        }
      });

      if (!hasTeam && !teamlessArr) {
        setTeamlessPlayers([...teamlessPlayers, currentPlayer]);
      }

      if (teamlessArr) {
        setRoster(sortTeams(roster));
      }
    }
  };

  const sortTeams = (roster) => {
    roster.sort((a, b) => {
      var nameA = userDisplayName(a?.name).toLowerCase();
      var nameB = userDisplayName(b?.name).toLowerCase();
      if (nameA > nameB) return 1;
      if (nameA < nameB) return -1;
      return 0;
    });

    roster.forEach((tempTeamPlayer) => {
      if (tempTeamPlayer.players?.length) {
        tempTeamPlayer.players.sort((a, b) => {
          var nameA = userDisplayName(a.firstName, a.lastName).toLowerCase();
          var nameB = userDisplayName(b.firstName, b.lastName).toLowerCase();
          if (nameA > nameB) return 1;
          if (nameA < nameB) return -1;
          return 0;
        });
      }
    });

    return roster;
  };

  // Initial page setup
  useEffect(() => {
    const check = async () => {
      try {
        if (event) {
          if (!organize && event.hideRoster) {
            postNotification("warning", t("rosterHiddenNotification"));
            var path = newPath({
              active_tab: "overview"
            });
            setActiveTab(0);
            navigate(path, { replace: false });
            return;
          }
          if (!roster) await handlePlayers();

          if (
            (pSocketStatus === "" && !pSocketUrl) ||
            ((event?.teamEvent || event?.doublesEvent) &&
              tSocketStatus === "" &&
              !tSocketUrl)
          )
            await handleWSConnection();

          if (organize && event.gameSystem?.hasFactions) {
            const { armyArray } = await getArmyPickerArmies(
              event?.gameSystem?.id || event?.gameSystemId
            );
            if (armyArray?.length) {
              setArmies(armyArray);
            }
          } else {
            setArmies(null);
          }
        }
      } catch (err) {
        if (pSocketStatus) setPSocketStatus("Disconnected");
        if (tSocketStatus) setTSocketStatus("Disconnected");
      } finally {
        setSubLoading(false);
        window.addEventListener("offline", () => {
          if (
            pSocketStatus !== "Connecting" ||
            ((event?.teamEvent || event?.doublesEvent) &&
              tSocketStatus !== "Connecting")
          ) {
            setPSocketStatus("Disconnected");
            setPSocketUrl("");
            setTSocketStatus("Disconnected");
            setTSocketUrl("");
          }
        });
      }
    };
    check();
    // eslint-disable-next-line
  }, []);

  // Runs whenever a new WS message is received
  useEffect(() => {
    handleMessages(pWebSocket);
    //eslint-disable-next-line
  }, [pWebSocket.lastMessage]);

  useEffect(() => {
    handleTeamMessages(tWebSocket);
    //eslint-disable-next-line
  }, [tWebSocket.lastMessage]);

  useEffect(() => {
    if (
      (event?.teamEvent || event?.doublesEvent) &&
      roster?.length &&
      teamlessPlayers?.length
    ) {
      teamlessPlayers.forEach((player) =>
        handlePlayersInTeams(roster, player, true)
      );
    }
    // eslint-disable-next-line
  }, [teamlessPlayers, roster]);

  // Updates counters whenever roster updates
  useEffect(() => {
    if (roster) {
      if (roster?.length) {
        let counters = {
          registered: 0,
          checkedin: 0,
          active: 0
        };

        roster.forEach((player) => {
          if (!player?.paymentPending) counters.registered++;
          if (player?.checkedIn && !player?.paymentPending)
            counters.checkedin++;
          if (player?.checkedIn && !player?.dropped && !player?.paymentPending)
            counters.active++;
        });

        setPlayerStats([
          {
            label: event?.teamEvent
              ? t("registeredTeams")
              : t("registeredPlayers"),
            value: counters?.registered || 0,
            color: "info"
          },
          {
            label: event?.teamEvent
              ? t("checkedInTeams")
              : t("checkedInPlayers"),
            value: counters?.checkedin || 0,
            color: "warning"
          },
          {
            label: event?.teamEvent ? t("activeTeams") : t("activePlayers"),
            value: counters?.active || 0,
            color: "success"
          }
        ]);
      }
    }
  }, [roster, event]);

  // Roster filtering
  useEffect(() => {
    if (roster) {
      switch (filterType) {
        case "Checked In":
          if (event?.doublesEvent || event?.teamEvent) {
            const tempArr = [];
            let playersCheckedIn = false;
            let teamCheckedIn = false;

            roster.forEach((teamPlayer) => {
              teamCheckedIn = false;
              playersCheckedIn = false;

              if (teamPlayer?.checkedIn) {
                teamCheckedIn = true;
              } else {
                teamCheckedIn = false;
              }

              if (teamPlayer?.players) {
                for (const _player of teamPlayer.players) {
                  if (_player?.checkedIn) {
                    playersCheckedIn = true;
                  } else {
                    playersCheckedIn = false;
                    break;
                  }
                }
              }

              if (playersCheckedIn && teamCheckedIn) tempArr.push(teamPlayer);

              setFilteredPlayers(tempArr);
            });
          } else {
            setFilteredPlayers(roster.filter((player) => player.checkedIn));
          }
          break;

        case "Not Checked In":
          if (event?.doublesEvent || event?.teamEvent) {
            const tempArr = [];

            roster.forEach((teamPlayer) => {
              if (!teamPlayer?.checkedIn && !teamPlayer?.dropped) {
                tempArr.push(teamPlayer);
              } else {
                if (teamPlayer?.players) {
                  for (const _player of teamPlayer.players) {
                    if (!_player?.checkedIn && !_player?.dropped) {
                      tempArr.push(teamPlayer);
                      break;
                    }
                  }
                }
              }
            });

            setFilteredPlayers(tempArr);
          } else {
            setFilteredPlayers(
              roster.filter((player) => !player.checkedIn && !player.dropped)
            );
          }
          break;

        case "Lists":
          if (event?.doublesEvent || event?.teamEvent) {
            const tempArr = [];
            let found = false;

            roster.forEach((teamPlayer) => {
              found = false;
              if (teamPlayer?.players) {
                for (const _player of teamPlayer.players) {
                  if (_player?.listId) {
                    found = true;
                  } else {
                    found = false;
                    break;
                  }
                }
              }

              if (found) tempArr.push(teamPlayer);
            });
            setFilteredPlayers(tempArr);
          } else {
            setFilteredPlayers(roster.filter((player) => player?.listId));
          }
          break;

        case "No Lists":
          if (event?.doublesEvent || event?.teamEvent) {
            const tempArr = [];

            roster.forEach((teamPlayer) => {
              if (teamPlayer?.players) {
                for (const _player of teamPlayer.players) {
                  if (!_player?.listId) {
                    tempArr.push(teamPlayer);
                    break;
                  }
                }
              }
            });

            setFilteredPlayers(tempArr);
          } else {
            setFilteredPlayers(roster.filter((player) => !player?.listId));
          }

          break;

        case "Dropped":
          setFilteredPlayers(roster.filter((player) => player.dropped));
          break;

        case "Not Dropped":
          setFilteredPlayers(roster.filter((player) => !player.dropped));
          break;

        default:
          setFilteredPlayers(null);
          setFilterType("");
      }
    }
    //eslint-disable-next-line
  }, [filterType, roster]);

  // Stops loading animation upon creation of new event where there are no teams or players yet
  useEffect(() => {
    if (pSocketStatus === "Open" && !roster) {
      setSubLoading(false);
      setRoster([]);
    }
  }, [pSocketStatus, roster]);

  return (
    <EventRosterDefault
      organize={organize}
      setActiveTab={setActiveTab}
      event={event}
      authContext={authContext}
      playerStats={playerStats}
      players={roster}
      setPlayers={setRoster}
      filteredPlayers={filteredPlayers}
      setFilteredPlayers={setFilteredPlayers}
      subLoading={subLoading}
      setPlayer={setPlayer}
      setTeamPlayer={setTeamPlayer}
      optionsAnchorEl={optionsAnchorEl}
      setOptionsAnchorEl={setOptionsAnchorEl}
      setLoading={setLoading}
      setBulkAddEmailsOpen={setBulkAddEmailsOpen}
      setBulkAddOpen={setBulkAddOpen}
      setRefreshPlayers={setRefreshPlayers}
      player={player}
      armies={armies}
      teamPlayer={teamPlayer}
      bulkAddOpen={bulkAddOpen}
      bulkAddEmailsOpen={bulkAddEmailsOpen}
      loading={loading}
      filtersAnchorEl={filtersAnchorEl}
      setFiltersAnchorEl={setFiltersAnchorEl}
      filterType={filterType}
      setFilterType={setFilterType}
      deletePlayer={deletePlayer}
      handleWSConnection={handleWSConnection}
      tSocketStatus={tSocketStatus}
      pSocketStatus={pSocketStatus}
      handlePlayers={handlePlayers}
      useWebSockets={true}
    />
  );
}
